// React, Next
import Head from 'next/head'
import { useRouter } from 'next/router'

// Config
import nextI18nextConfig from 'next-i18next.config'
import { localBusinessSchema, ogImage, siteUrl } from 'lib/seoConfigs'

type Props = {
  title: string
  description: string
  /** Locale-specific paths, without leading or trailing slashes. */
  dePath: string
  enPath: string
  /** Open Graph type; articles should pass "article". */
  ogType?: 'website' | 'article'
  /** Extra JSON-LD emitted alongside the LocalBusiness block. */
  schema?: Record<string, unknown>
}

/**
 * The full head block every page needs: canonical, hreflang, Open Graph,
 * Twitter card and structured data. Kept in one place so pages cannot drift
 * apart - several used to ship without a canonical at all.
 */
export default function SeoHead(props: Props) {
  const router = useRouter()
  const currentLocale = Array.isArray(router.query.locale)
    ? router.query.locale[0]
    : router.query.locale || nextI18nextConfig.i18n.defaultLocale

  const deUrl = `${siteUrl}/de/${props.dePath}/`
  const enUrl = `${siteUrl}/en/${props.enPath}/`
  const canonical = currentLocale === 'de' ? deUrl : enUrl

  return (
    <Head>
      {/* Basic Meta */}
      <title>{props.title}</title>
      <meta name="description" content={props.description} />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta httpEquiv="content-language" content={currentLocale} />
      <meta
        name="robots"
        content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
      />

      {/* Favicon */}
      <link rel="shortcut icon" href="/favicon.ico" />

      {/* Canonical */}
      <link rel="canonical" href={canonical} />

      {/* Hreflang for localization */}
      <link rel="alternate" hrefLang="de" href={deUrl} />
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="x-default" href={deUrl} />

      {/* Open Graph */}
      <meta property="og:type" content={props.ogType ?? 'website'} />
      <meta property="og:title" content={props.title} />
      <meta property="og:description" content={props.description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta
        property="og:locale"
        content={currentLocale === 'de' ? 'de_CH' : 'en_US'}
      />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={props.title} />
      <meta name="twitter:description" content={props.description} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(localBusinessSchema)
        }}
      />
      {props.schema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(props.schema) }}
        />
      )}
    </Head>
  )
}
